/**
 * lib/numbering.ts
 * ------------------------------------------------------------------
 * Gapless, collision-free document numbering.
 *
 * A tax-compliant invoice register must have sequential numbers with
 * no gaps and no duplicates, even under concurrent issue. We achieve
 * that with a Firestore transaction against a per-series, per-year
 * counter document at `counters/{series}-{year}`.
 *
 * Numbers are assigned ONLY when a document is issued (drafts have a
 * null number), and a voided document keeps its number forever — the
 * sequence is never rewound or reused. That is exactly the property
 * BURS's continuous-transaction model will expect.
 *
 * Format: PREFIX-YEAR-SEQ, e.g. INV-2026-0001, RCT-2026-0007.
 * The sequence resets each calendar year; the year is embedded so the
 * full identifier is still globally unique and chronologically
 * sortable.
 * ------------------------------------------------------------------
 */
import { doc, runTransaction } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS, type Counter, type NumberSeries } from '@/types';

const SERIES_PREFIX: Record<NumberSeries, string> = {
  invoice: 'INV',
  receipt: 'RCT',
  quote: 'QUO',
  creditNote: 'CRN',
};

const DEFAULT_PADDING = 4;

function counterId(series: NumberSeries, year: number): string {
  return `${series}-${year}`;
}

function format(prefix: string, year: number, seq: number, padding: number): string {
  return `${prefix}-${year}-${String(seq).padStart(padding, '0')}`;
}

/**
 * Atomically reserve and return the next number for a series.
 *
 * Runs inside a transaction so two concurrent issues can never collide
 * or skip. Creates the counter document on first use for the year.
 *
 * @param series  which document series
 * @param when    the document's date (defaults to now); determines the
 *                year bucket so back-dated documents land in the right
 *                yearly sequence
 */
export async function nextNumber(
  series: NumberSeries,
  when: Date = new Date(),
): Promise<string> {
  const year = when.getFullYear();
  const prefix = SERIES_PREFIX[series];
  const ref = doc(db, COLLECTIONS.counters, counterId(series, year));

  const assigned = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      const fresh: Counter = {
        series,
        year,
        next: 2, // we are about to hand out #1
        prefix,
        padding: DEFAULT_PADDING,
      };
      tx.set(ref, fresh);
      return format(prefix, year, 1, DEFAULT_PADDING);
    }
    const data = snap.data() as Counter;
    const seq = data.next;
    tx.update(ref, { next: seq + 1 });
    return format(prefix, year, seq, data.padding ?? DEFAULT_PADDING);
  });

  return assigned;
}

/**
 * Peek at the next number for a series WITHOUT consuming it — useful
 * for showing "this will be INV-2026-0008" in a draft before issue.
 * Not transactional; the real number is reserved by `nextNumber` at
 * issue time, so treat this as indicative only.
 */
export async function peekNextNumber(
  series: NumberSeries,
  when: Date = new Date(),
): Promise<string> {
  const year = when.getFullYear();
  const prefix = SERIES_PREFIX[series];
  const ref = doc(db, COLLECTIONS.counters, counterId(series, year));
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  const seq = snap.exists() ? (snap.data() as Counter).next : 1;
  return format(prefix, year, seq, DEFAULT_PADDING);
}
